/**
 * « Réclamations fournisseur » on the pharmacy plane (/achats/reclamations, /nouvelle, /:id, the §B2
 * drawer of S1 / S3) — screen CONFIG (design/ux/03 §5.2-§5.7, §6.6, §7; refonte WP-35).
 * Platform-governed UI values only: the business vocabulary and bounds (states, motifs, channels,
 * views, page sizes, sort whitelist, attachment limits) are the shared twin
 * `@pharmakon/shared/constants/reclamations.js`, held equal to the server CONFIG by
 * `apps/server/src/__tests__/reclamations.parity.test.js` — never retyped here (PK-BUG-15).
 */
import {
  MOTIF_RECLAMATION,
  RECLAMATION_BORNES,
  TYPES_PIECE_JOINTE_RECLAMATION,
  VUE_JOURNAL_RECLAMATION,
  VUE_PORTAIL_RECLAMATION,
} from "@pharmakon/shared/constants/reclamations.js";
import { IMPRESSION_LIGNES_MAX } from "@/config/achat-journal.config.js";
import { PRESETS_PERIODE_AVOIRS } from "@/config/avoir-journal.config.js";

export { RECLAMATION_BORNES };

/** ux/03 §5.4 — the views of the journal, in their reading order (exclusive: the server's `vue`). */
export const ORDRE_VUES_RECLAMATION = Object.freeze([
  VUE_JOURNAL_RECLAMATION.OUVERTES,
  VUE_JOURNAL_RECLAMATION.A_TRAITER,
  VUE_JOURNAL_RECLAMATION.CLOTUREES,
  VUE_JOURNAL_RECLAMATION.TOUTES,
]);

/**
 * The view of a journal opened without one: the work list « Ouvertes » (ux/03 §5.4 lists it first;
 * no default period — an open réclamation stays visible whatever its age). DECISIONS A-62 (1).
 */
export const VUE_DEFAUT_RECLAMATION = VUE_JOURNAL_RECLAMATION.OUVERTES;

/** ux/03 §5.1 / D-20 — the motif chips, in the design's order (the composer's radio group). */
export const ORDRE_MOTIFS_RECLAMATION = Object.freeze([
  MOTIF_RECLAMATION.MANQUANT,
  MOTIF_RECLAMATION.CASSE,
  MOTIF_RECLAMATION.PERIME,
  MOTIF_RECLAMATION.ERREUR_PRODUIT_LOT,
  MOTIF_RECLAMATION.PRIX,
  MOTIF_RECLAMATION.DOUBLON,
  MOTIF_RECLAMATION.RETARD,
  MOTIF_RECLAMATION.AUTRE,
]);

/** OP-03-26 — the motif whose description is required. */
export const MOTIF_DESCRIPTION_REQUISE = MOTIF_RECLAMATION.AUTRE;

/** ux/03 §5.4 « Période » — the purchase documents' presets (one list, the avoir journal's). */
export const PRESETS_PERIODE_RECLAMATIONS = PRESETS_PERIODE_AVOIRS;

/** The URL keys of the journal (filters are `useFilters`'; these are the page's own). */
export const PARAMS_JOURNAL_RECLAMATIONS = Object.freeze({ VUE: "vue", PAGE: "page" });

/** The default sort of the grid (server whitelist TRIS_JOURNAL_RECLAMATION): the reference date, newest first. */
export const TRI_DEFAUT_RECLAMATIONS = Object.freeze({ by: "date", order: "desc" });

/**
 * ux/03 §5.4 — below this content width (1024 layout: 822 px of columns) « Facture » and
 * « Activité » leave the grid for the row tooltip and the detail, and the footer is compact.
 */
export const LARGEUR_COMPACTE_RECLAMATIONS = 1000;

/** ux/03 §5.5 — below this content width the document is one column with « Détail · Historique » tabs. */
export const LARGEUR_DOCUMENT_UNE_COLONNE = 1000;

/** §6.6 print — réclamations read for one printed journal (the purchase journals' browser bound). */
export const IMPRESSION_RECLAMATIONS_MAX = IMPRESSION_LIGNES_MAX;

/**
 * ux/03 §5.2 — the URL keys of the full-page composer /achats/reclamations/nouvelle (the entry points
 * pre-fill it: a facture line « Signaler », a bon de retour, an e-facture, « Nouvelle réclamation liée »).
 */
export const PARAMS_COMPOSEUR = Object.freeze({
  FOURNISSEUR: "fournisseur_id",
  FACTURE: "facture_achat_id",
  LIGNES: "lignes",
  MOTIF: "motif",
  E_FACTURE: "e_facture_id",
  BON_COMMANDE: "bon_commande_id",
  BON_RETOUR: "bon_retour_id",
  OBJET: "objet",
});

/** OP-03-32 — what the file picker offers (the server decides by the bytes). */
export const ACCEPT_PIECES_JOINTES = Object.freeze([...TYPES_PIECE_JOINTE_RECLAMATION, ".jpg", ".jpeg", ".png", ".pdf"]).join(",");

/** File extensions the composer pre-checks a file by when the browser gives no type. */
export const EXTENSIONS_PIECES_JOINTES = Object.freeze({ jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", pdf: "application/pdf" });

/** ux/03 §5.3 — the search debounce of the facture picker (the house value of the purchase screens). */
export const RECHERCHE_FACTURE_DEBOUNCE_MS = 250;

// ─── Fournisseur portal — « Réclamations reçues » (design/ux/03 §5.8; refonte WP-36) ───────────────

/** The portal's routes (the notifications' deep link is the server's `URL_RECLAMATION_PORTAIL`). */
export const URL_RECLAMATIONS_RECUES = "/portail-fournisseur/reclamations";
export const urlReclamationRecue = (id) => `${URL_RECLAMATIONS_RECUES}/${encodeURIComponent(id)}`;

/** ux/03 §5.8 — the views of « Réclamations reçues », in their reading order (the server's `vue`). */
export const ORDRE_VUES_PORTAIL = Object.freeze([
  VUE_PORTAIL_RECLAMATION.A_TRAITER,
  VUE_PORTAIL_RECLAMATION.EN_COURS,
  VUE_PORTAIL_RECLAMATION.RESOLUES,
  VUE_PORTAIL_RECLAMATION.REJETEES,
  VUE_PORTAIL_RECLAMATION.TOUTES,
]);

/**
 * The view of a list opened without one: the supplier's work list « À traiter » (ux/03 §5.8 lists it
 * first; the menu badge counts it). DECISIONS A-63 (1).
 */
export const VUE_DEFAUT_PORTAIL = VUE_PORTAIL_RECLAMATION.A_TRAITER;

/** ux/03 §5.8 — the list's server sort keys (the shared whitelist, minus the pharmacy's own columns). */
export const TRIS_PORTAIL_RECLAMATION = Object.freeze(["date", "numero", "pharmacie", "montant_reclame", "activite"]);
export const TRI_DEFAUT_PORTAIL = TRI_DEFAUT_RECLAMATIONS;

/**
 * ux/03 §5.8 « 1024: N° · Reçue le · Pharmacie · Motif · Réclamé · État » — below this content width
 * « Facture » and « Activité » leave the grid for the row tooltip (the journal's own breakpoint).
 */
export const LARGEUR_COMPACTE_PORTAIL = LARGEUR_COMPACTE_RECLAMATIONS;

/**
 * ux/03 §5.8 « Émettre une note de crédit sur l'e-facture {EF-…} » — the URL keys the portal's
 * « Nouvel avoir » wizard (`pages/fournisseur/finance/AvoirManager.jsx`) is pre-filled from.
 */
export const PARAMS_NOTE_CREDIT = Object.freeze({
  OUVRIR: "nouvel_avoir",
  E_FACTURE: "e_facture_id",
  MONTANT: "montant_ttc",
  MOTIF: "motif",
});
export const URL_AVOIRS_PORTAIL = "/portail-fournisseur/finance/avoirs";
